import { useTranslation } from 'react-i18next';

import { Form } from '@arco-design/web-react';
import type { FormInstance } from '@arco-design/web-react';

import {
  NIC_CONFIGURATION_FIELD,
  NicConfiguration,
  type NicConfigurationProps,
  type NicConfigurationValue,
} from '@/components-biz/NicConfiguration';

import { useScheduleResourceFormPriceRefresh } from '../../platform/resource-form';
import { VendorDeviceSelector } from '../resourceConfig/VendorDeviceSelector';
import { hasVendorDeviceSelection } from '../vendorAndDeviceRadioList';
import { vendorDeviceMetaFromSelection } from '../vendorDevice';
import {
  type NicPricingByDeviceType,
  getNicPricingFieldKey,
  getNicPricingFieldPath,
  getNicPricingMeta,
  isNicPricingComplete,
} from './utils';

/** 带厂商设备计价选型的网卡配置表单属性。 */
export interface NicPricingFormItemProps {
  form: FormInstance;
  billModelId?: string;
  deploymentTypeId?: string;
  /**
   * 下单编辑模式：
   * - `order-fixed`：整块只读（厂商设备 + 网段）
   * - `order-custom` / `order-post-paid`：SKU 预置已有厂商设备时锁定厂商设备，网段不限制
   */
  resourceEditMode?: string;
  /** SKU 预置快照；自定义/后付费据此判断厂商设备是否预置（非用户当前选择） */
  presetConfigSnapshot?: Record<string, unknown>;
  /** 显式强制厂商设备只读（兼容旧调用；优先配合 resourceEditMode） */
  disabled?: boolean;
  skipFetch?: boolean;
  /** 表单写回后是否主动刷新询价；仅用于通用监听可能尚未完成首次快照的下单场景。 */
  refreshPriceOnChange?: boolean;
  /** 网卡列表字段，默认 network.nics；节点抽屉 embedded 为 resource.nics */
  nicsField?: string;
  /** 网卡计价字段所在表单，默认与 form 相同；embedded 抽屉计价写 parentForm */
  pricingForm?: FormInstance;
}

type NicConfigurationWithRefreshProps = NicConfigurationProps & { afterChange?: () => void };

function NicConfigurationWithRefresh({ onChange, afterChange, ...props }: NicConfigurationWithRefreshProps) {
  return (
    <NicConfiguration
      {...props}
      onChange={(value) => {
        onChange?.(value);
        afterChange?.();
      }}
    />
  );
}

function readPresetNicPricing(preset?: Record<string, unknown>): NicPricingByDeviceType | undefined {
  const network = preset?.network as { nicPricing?: NicPricingByDeviceType } | undefined;
  return network?.nicPricing;
}

/** SKU / 下单共用的网卡计价配置，固定写入 network.nics 与 network.nicPricing。 */
export function NicPricingFormItem({
  form,
  billModelId,
  deploymentTypeId,
  resourceEditMode,
  presetConfigSnapshot,
  disabled,
  skipFetch,
  refreshPriceOnChange,
  nicsField = NIC_CONFIGURATION_FIELD,
  pricingForm,
}: NicPricingFormItemProps) {
  const { t } = useTranslation();
  const effectivePricingForm = pricingForm ?? form;
  const pricingByDeviceType = Form.useWatch('network.nicPricing', effectivePricingForm) as NicPricingByDeviceType | undefined;
  const schedulePriceRefresh = useScheduleResourceFormPriceRefresh();
  const refreshPrice = () => {
    if (refreshPriceOnChange) schedulePriceRefresh(effectivePricingForm);
  };

  /** 固定规格：厂商设备与网段整块只读 */
  const isOrderFixed = resourceEditMode === 'order-fixed' || disabled === true;
  /** 自定义/后付费：SKU 预置已有厂商设备时锁定厂商设备，网段仍可改 */
  const lockVendorWhenPreset = resourceEditMode === 'order-custom' || resourceEditMode === 'order-post-paid';
  const presetNicPricing = readPresetNicPricing(presetConfigSnapshot);

  return (
    <Form.Item
      field={nicsField}
      rules={[
        {
          validator: (value, callback) => {
            const nicConfigurations = value as NicConfigurationValue | undefined;
            if (!nicConfigurations?.length) {
              return callback(t('data-center:please.select.network.interface.card'));
            }
            return callback(
              isNicPricingComplete(nicConfigurations, pricingByDeviceType)
                ? undefined
                : t('data-center:training.cluster.nic.segment.required'),
            );
          },
        },
      ]}
    >
      <NicConfigurationWithRefresh
        readOnly={isOrderFixed}
        getSelectedVendorDevice={(deviceType) => getNicPricingMeta(pricingByDeviceType, deviceType)}
        renderVendorDeviceSelector={(deviceType) => {
          const pricingFieldKey = getNicPricingFieldKey(deviceType);
          const presetVendorFilled = hasVendorDeviceSelection(getNicPricingMeta(presetNicPricing, deviceType));
          const vendorLocked = isOrderFixed || (lockVendorWhenPreset && presetVendorFilled);
          return (
            <VendorDeviceSelector
              form={effectivePricingForm}
              resourceCode="NIC"
              deviceType={deviceType}
              vendorDevicePrefix={getNicPricingFieldPath(deviceType)}
              billModelId={billModelId}
              deploymentTypeId={deploymentTypeId}
              disabled={vendorLocked}
              skipFetch={skipFetch === true || vendorLocked}
              compactBottom
              onPricingSelectionChange={(selection) => {
                const nextSelection = vendorDeviceMetaFromSelection(selection);
                const currentPricing = (effectivePricingForm.getFieldValue('network.nicPricing') || {}) as NicPricingByDeviceType;
                const nextPricing = { ...currentPricing };
                if (nextSelection) nextPricing[pricingFieldKey] = nextSelection;
                else delete nextPricing[pricingFieldKey];
                effectivePricingForm.setFieldValue('network.nicPricing', nextPricing);
                refreshPrice();
              }}
            />
          );
        }}
        afterChange={refreshPrice}
      />
    </Form.Item>
  );
}
